import React from 'react'
// libs
import { useSelector } from 'react-redux'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// types
import { FieldType, IField, IFieldLink } from 'types/graphql'

export const useRelatedFields = () => {
  const { riskView } = React.useContext(RiskRegisterContext)

  const riskTemplates = useSelector(riskTemplatesSelector)

  // All template ids referenced by the risk view — used to render related forms.
  const allFormsIds = React.useMemo(() => {
    const ids = riskView?.settings?.fields?.map((field) => field.riskTemplateId).filter(Boolean) ?? []

    return [...new Set(ids)]
  }, [riskView])

  // Template ids that expose at least one inline-editable field (excluding sections/tables).
  const relatedFormsIds = React.useMemo(() => {
    const ids = riskView?.settings?.fields?.filter((field) => {
        const template = riskTemplates.find((item) => item.id === field.riskTemplateId)
        const fieldType = template?.fields.find((item) => item.id === field.fieldId)?.fieldType

        return ![FieldType.Section, FieldType.Table].includes(fieldType)
      })
      .map((field) => field.riskTemplateId)
      .filter(Boolean) ?? []

    // Follow the store order like relatedForms (header, view modal) do — settings.fields order is arbitrary
    return riskTemplates.filter((item) => ids.includes(item.id)).map((item) => item.id)
  }, [riskView, riskTemplates])

  const relatedForms = React.useMemo(() => {
    return riskTemplates
      .filter((item) => allFormsIds.includes(item.id))
      .map((template) => {
        const fieldLinks = template?.fieldLinks?.map((item: IFieldLink) => ({
          ...item.field,
          parentFieldId: item.parentFieldId,
          position: item.position,
          label: item.fieldLabel,
          fieldLinkId: item.id,
          primary: item.primary
        })) || []

        return {
          id: template.id,
          name: template.name,
          fields: [...template.fields, ...fieldLinks]
        }
      })
  }, [riskTemplates, allFormsIds])

  const relatedFields: IField[] = React.useMemo(
    () =>
      relatedForms.flatMap((item) =>
        item.fields.map((f) => ({
          ...f,
          label: `${item.name}: ${f.label}`,
          riskTemplateId: item.id
        }))
      ) || [],
    [relatedForms]
  )

  return { relatedFormsIds, relatedForms, relatedFields }
}
